import { INFECTION_TEXT_LIMITS, ISOLATION_FLAG_STATUSES } from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema, reasonSchema, RECORD_STATUSES } from './clinical-common.ts';
import { capturedListValueSchema, operationsListItemSchema } from './operations.ts';

// Infection control (docs/plans/phase3-operations-modules.md §2.3). Notes and organisms
// travel in bodies only, never in URLs.

export const infectionOptionsSchema = z.object({
  precautions: z.array(operationsListItemSchema),
  categories: z.array(operationsListItemSchema),
});
export type InfectionOptionsView = z.infer<typeof infectionOptionsSchema>;

export const isolationFlagSchema = z.object({
  id: z.uuid(),
  precaution: capturedListValueSchema,
  /** "Contact precautions". */
  words: z.string(),
  visit: z.object({ id: z.uuid(), visitNumber: z.string(), open: z.boolean() }).nullable(),
  note: z.string().nullable(),
  startedAt: z.iso.datetime(),
  startedBy: actorSchema,
  status: z.enum(ISOLATION_FLAG_STATUSES),
  /** Whether it shows on the banner and bed board now. */
  showing: z.boolean(),
  statusReason: z.string().nullable(),
  statusChangedAt: z.iso.datetime().nullable(),
  statusChangedBy: actorSchema.nullable(),
});
export type IsolationFlagView = z.infer<typeof isolationFlagSchema>;

export const isolationFlagListSchema = z.object({ items: z.array(isolationFlagSchema) });
export type IsolationFlagListView = z.infer<typeof isolationFlagListSchema>;

export const startIsolationFlagRequestSchema = z.object({
  precautionCode: z.string().trim().min(1).max(40),
  /** Tie it to this open visit: it then stops showing when the visit closes. */
  visitId: z.uuid().nullable(),
  note: z.string().trim().max(INFECTION_TEXT_LIMITS.note).nullable(),
});
export type StartIsolationFlagRequest = z.infer<typeof startIsolationFlagRequestSchema>;

export const endIsolationFlagRequestSchema = z.object({ reason: reasonSchema });

export const recordInfectionEventRequestSchema = z.object({
  categoryCode: z.string().trim().min(1).max(40),
  onsetOn: isoDaySchema,
  organism: z.string().trim().max(INFECTION_TEXT_LIMITS.organism).nullable(),
  note: z.string().trim().max(INFECTION_TEXT_LIMITS.note).nullable(),
});
export type RecordInfectionEventRequest = z.infer<typeof recordInfectionEventRequestSchema>;

/** A patient on the line list: shown, or hidden under the sensitivity rule (ADR 0036). */
export const linePatientSchema = z
  .object({ id: z.uuid(), displayName: z.string(), patientNumber: z.string() })
  .nullable();

export const infectionEventSchema = z.object({
  id: z.uuid(),
  admissionId: z.uuid(),
  patient: linePatientSchema,
  wardName: z.string().nullable(),
  category: capturedListValueSchema,
  onsetOn: isoDaySchema,
  admittedAt: z.iso.datetime(),
  /** EAT days from the admission day to onset. */
  daysFromAdmission: z.number().int(),
  organism: z.string().nullable(),
  note: z.string().nullable(),
  status: z.enum(RECORD_STATUSES),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
});
export type InfectionEventView = z.infer<typeof infectionEventSchema>;

const countRowSchema = z.object({ key: z.string(), label: z.string(), count: z.number().int() });

export const infectionLineListSchema = z.object({
  from: isoDaySchema,
  to: isoDaySchema,
  /** Active events whose onset is in the range, the latest onset first. */
  events: z.array(infectionEventSchema),
  counts: z.object({
    total: z.number().int(),
    byCategory: z.array(countRowSchema),
    byWard: z.array(countRowSchema),
  }),
  /** Flags showing now at this facility. */
  flags: z.array(
    z.object({
      id: z.uuid(),
      patient: linePatientSchema,
      words: z.string(),
      startedAt: z.iso.datetime(),
      wardName: z.string().nullable(),
    }),
  ),
});
export type InfectionLineListView = z.infer<typeof infectionLineListSchema>;

/** Current stays, for recording an infection (`infection.record`). */
export const infectionStaysSchema = z.object({
  items: z.array(
    z.object({
      admissionId: z.uuid(),
      patient: linePatientSchema,
      wardName: z.string().nullable(),
      bedLabel: z.string().nullable(),
      admittedAt: z.iso.datetime(),
    }),
  ),
});
export type InfectionStaysView = z.infer<typeof infectionStaysSchema>;
